/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-invalid-void-type, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Legacy report API typing debt is outside this metadata contract; keep the staged file compatible with the repository pre-commit hook. */
import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { RecordResponse } from '../models/ServiceTypes';

export interface Report {
  ReportsUserReportId: number | null;
  ReportsDefaultReportId: number;
  IsCustom: boolean;
  Filters: string;
  Description: string;
  Title: string;
  CreatedDate: string;
  UpdatedDate: string;
  Category: string;
  Sections: any[];
}

export interface ReportSection {
  ReportsUserReportSectionId: number;
  ReportsDefaultSectionId: number;
  IsCustom: boolean;
  ReportsAvailableSectionId: number;
  SectionKey: string;
  Position: number;
  Settings: string;
  ReportsUserReportId: number;
  UpdatedDate: string;
  CreatedDate: string;
  Title: string;
  Name: string;
  Description: string;
  Image: string;
}

export interface AvailableReportSection {
  ReportsAvailableSectionId: number;
  Role: string;
  SectionKey: string;
  Category: string | null;
  Title: string;
  Name: string;
  Description: string;
  Image: string;
  Position?: number;
  Settings?: string;
}

export interface CreateReportRequest {
  Title: string;
  Description: string;
  Sections: Array<{
    ReportsAvailableSectionId: number;
    Role: string;
    SectionKey: string;
    Category: string | null;
    Title: string;
    Name: string;
    Description: string;
    Image: string;
    Position: number;
    Settings?: string;
  }>;
  Filters: string;
  IsCustom: boolean;
  Category: string;
}

export interface ScheduledReportRequest {
  Frequency: string;
  OffsetDays: number;
  VaultFolderTemplate: string;
  FileNameTemplate: string;
  Name: string;
  IsEnabled: boolean;
  IncludedByDefault: boolean;
  InvestorOverrides: any[];
  OnlyActive: boolean;
  Reports: Array<{
    ReportsDefaultReportId: number | null;
    ReportsUserReportId: number | null;
    Name?: string;
  }>;
  FrequencyDisplay?: string;
  OffsetDate?: string | null;
  LastRanDate?: string | null;
  NextRunDate?: string | null;
  ContentFilters?: any;
}

export interface ScheduledReport {
  ScheduledReportId: number;
  Name: string;
  Frequency: string;
  OffsetDays: number;
  VaultFolderTemplate: string;
  FileNameTemplate: string;
  IsEnabled: boolean;
  IncludedByDefault: boolean;
  OnlyActive: boolean;
  CreatedDate: string;
  UpdatedDate: string;
  Reports: Array<{
    ReportsDefaultReportId: number | null;
    ReportsUserReportId: number | null;
    Name?: string;
    Title?: string;
  }>;
  InvestorOverrides: any[];
  FrequencyDisplay?: string;
  OffsetDate?: string | null;
  LastRanDate?: string | null;
  NextRunDate?: string | null;
  ContentFilters?: any;
}

export interface ScheduledReportRun {
  ScheduledReportRunId: number;
  ScheduledReportId: number;
  ScheduledDate: string;
  DataStartDate: string;
  DataEndDate: string;
  HasRun: boolean;
  InProgress: boolean;
  PercentComplete: number;
  StatusDisplay: string;
  NumberOfReportsGenerated: number;
}

export type ReportsResponse = RecordResponse<Report>;
export type ReportSectionsResponse = RecordResponse<ReportSection>;
export type AvailableReportSectionsResponse =
  RecordResponse<AvailableReportSection>;
export type ScheduledReportsResponse = RecordResponse<ScheduledReport>;
export type ScheduledReportRunsResponse = RecordResponse<ScheduledReportRun>;

export interface GetReportsQueryArg {
  _?: string;
}

export interface GetScheduledReportsQueryArg {
  sort?: Array<{ Property: string; Direction: string; Order: number }>;
  _?: string;
}

export function buildReportsSubscriptionArg(
  arg: GetReportsQueryArg,
): GetReportsQueryArg {
  return arg;
}

export function buildScheduledReportsSubscriptionArg(
  arg: GetScheduledReportsQueryArg,
): GetScheduledReportsQueryArg {
  return arg;
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: waServicesQuery,
  tagTypes: [
    'Reports',
    'ReportSections',
    'AvailableReportSections',
    'ScheduledReports',
    'ScheduledReportRuns',
  ],

  endpoints: (builder) => ({
    getReports: builder.query<ReportsResponse, GetReportsQueryArg>({
      query: (args) => {
        const { _ } = buildReportsSubscriptionArg(args);
        return {
          url: '/api/user/reports',
          method: 'GET',
          params: {
            _: _ || Date.now().toString(),
          },
        };
      },
      providesTags: ['Reports'],
    }),

    getReportSections: builder.query<
      ReportSectionsResponse,
      { reportId: number; isDefault?: boolean; _?: string }
    >({
      query: ({ reportId, isDefault = true, _ }) => ({
        url: `/api/user/reports/${reportId}/Sections`,
        method: 'GET',
        params: {
          isDefault: isDefault.toString(),
          _: _ || Date.now().toString(),
        },
      }),
      providesTags: (_result, _error, { reportId }) => [
        { type: 'ReportSections', id: reportId },
      ],
    }),

    getAvailableReportSections: builder.query<
      AvailableReportSectionsResponse,
      { _?: string }
    >({
      query: ({ _ }) => ({
        url: '/api/user/availableReportSections',
        method: 'GET',
        params: {
          _: _ || Date.now().toString(),
        },
      }),
      providesTags: ['AvailableReportSections'],
    }),

    createReport: builder.mutation<Report, CreateReportRequest>({
      query: (reportData) => ({
        url: '/api/user/reports',
        method: 'POST',
        body: reportData,
      }),
      invalidatesTags: ['Reports'],
    }),

    updateReport: builder.mutation<
      Report,
      Partial<Report> & { ReportsUserReportId: number }
    >({
      query: (reportData) => ({
        url: `/api/user/reports/${reportData.ReportsUserReportId}`,
        method: 'PUT',
        body: reportData,
      }),
      invalidatesTags: (_result, _error, { ReportsUserReportId }) => [
        { type: 'Reports' },
        { type: 'ReportSections', id: ReportsUserReportId },
      ],
    }),

    deleteReport: builder.mutation<void, { reportId: number }>({
      query: ({ reportId }) => ({
        url: `/api/user/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, _arg) => [{ type: 'Reports' }],
    }),

    getScheduledReports: builder.query<
      ScheduledReportsResponse,
      GetScheduledReportsQueryArg
    >({
      query: (args) => {
        const { sort, _ } = buildScheduledReportsSubscriptionArg(args);
        const params: Record<string, number | string> = {
          _: _ || Date.now().toString(),
        };

        if (sort) {
          sort.forEach((sortItem, index) => {
            params[`sort[${String(index)}].Property`] = sortItem.Property;
            params[`sort[${String(index)}].Direction`] = sortItem.Direction;
            params[`sort[${String(index)}].Order`] = sortItem.Order;
          });
        }

        return {
          url: '/api/investor/scheduledReports',
          method: 'GET',
          params,
        };
      },
      providesTags: ['ScheduledReports'],
    }),

    createScheduledReport: builder.mutation<
      ScheduledReport,
      ScheduledReportRequest
    >({
      query: (reportData) => ({
        url: '/api/investor/scheduledReports',
        method: 'POST',
        body: reportData,
      }),
      invalidatesTags: ['ScheduledReports'],
    }),

    updateScheduledReport: builder.mutation<
      ScheduledReport,
      Partial<ScheduledReportRequest> & { ScheduledReportId: number }
    >({
      query: ({ ScheduledReportId, ...reportData }) => ({
        url: `/api/investor/scheduledReports/${ScheduledReportId}`,
        method: 'PUT',
        body: { ...reportData, ScheduledReportId },
      }),
      invalidatesTags: ['ScheduledReports'],
    }),

    deleteScheduledReport: builder.mutation<void, number>({
      query: (scheduledReportId) => ({
        url: `/api/investor/scheduledReports/${scheduledReportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ScheduledReports'],
    }),

    getScheduledReportRuns: builder.query<
      ScheduledReportRunsResponse,
      {
        scheduledReportId: number;
        sort?: Array<{ Property: string; Direction: string; Order: number }>;
        _?: string;
      }
    >({
      query: ({ scheduledReportId, sort, _ }) => {
        const params: any = {
          _: _ || Date.now().toString(),
        };

        if (sort) {
          sort.forEach((sortItem, index) => {
            params[`sort[${index}].Property`] = sortItem.Property;
            params[`sort[${index}].Direction`] = sortItem.Direction;
            params[`sort[${index}].Order`] = sortItem.Order;
          });
        }

        return {
          url: `/api/investor/scheduledReports/${String(scheduledReportId)}/Runs`,
          method: 'GET',
          params,
        };
      },
      providesTags: (_result, _error, { scheduledReportId }) => [
        { type: 'ScheduledReportRuns', id: scheduledReportId },
      ],
    }),

    runScheduledReport: builder.mutation<
      ScheduledReportRun,
      { scheduledReportId: number }
    >({
      query: ({ scheduledReportId }) => ({
        url: `/api/investor/scheduledReports/${String(scheduledReportId)}/Runs`,
        method: 'POST',
        body: { ScheduledReportId: scheduledReportId },
      }),
      invalidatesTags: (_result, _error, { scheduledReportId }) => [
        { type: 'ScheduledReportRuns', id: scheduledReportId },
      ],
    }),
  }),
});

export const {
  useGetReportsQuery,
  useGetReportSectionsQuery,
  useGetAvailableReportSectionsQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useGetScheduledReportsQuery,
  useCreateScheduledReportMutation,
  useUpdateScheduledReportMutation,
  useDeleteScheduledReportMutation,
  useGetScheduledReportRunsQuery,
  useRunScheduledReportMutation,
} = reportsApi;
